import styled from 'styled-components'
import React, { useEffect, useState, useRef, UIEventHandler } from 'react'
import TimelineElement from './TimelineElement'
import { csvParse } from 'd3-dsv'
import { useSpring, animated, useTransition } from '@react-spring/web'
import { useDrag } from '@use-gesture/react'
import { MdSwipeLeft } from 'react-icons/md'
import ProgressBar from './ProgressBar'
import useIsMobile from 'hooks/useIsMobile'
import { device } from '../breakpoints'

declare global {
  interface Window {
    dataLayer: any
  }
}

const ExampleComponent = () => {
  const isMobile = useIsMobile()
  const [items, setItems] = useState<TimelineItem[]>([])
  const [grabbing, setGrabbing] = useState<boolean>(false)
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null)
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)
  const [showTip, setShowTip] = useState(true)
  const [{ x }, api] = useSpring(() => ({ x: 0 }))
  const transition = useTransition(showTip, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 200 },
  })
  const xOffset = useRef(0)
  const [pct, setPct] = useState(0)

  const bind = useDrag(
    ({ down, movement: [mx], first }) => {
      if (first) {
        xOffset.current = x.get()
        // console.log('First', xOffset.current)

        if (xOffset.current !== 0 && showTip) {
          setShowTip(false)
        }
      }

      if (x.get() > 0) {
        api.start({ x: 0 })
        return
      } else if (x.get() < -contentRef?.offsetWidth! + window.innerWidth) {
        api.start({ x: -contentRef?.offsetWidth! + window.innerWidth })
        return
      }

      api.start({ x: mx + xOffset.current })
      // console.log('spring x:', x.get(), 'mx:', mx, 'xOffset:', xOffset.current)
      setGrabbing(down)
      setPct((x.get() / (-contentRef?.offsetWidth! + window.innerWidth)) * 100)
    },
    { axis: 'x', threshold: 10, enabled: !isMobile }
  )

  const range =
    items.length > 1
      ? [
          [items[0].month, items[0].year],
          [items[items.length - 1].month, items[items.length - 1].year],
        ]
      : null

  const totalMonths = range
    ? (range[1][1] - range[0][1] + 1) * 12 -
      range[0][0] -
      (12 - range[1][0] - 1)
    : null

  const months = range
    ? new Array(totalMonths).fill(0).map((_, index) => {
        const month = ((range[0][0] - 1 + index) % 12) + 1
        const year = Math.floor(index / 12) + range[0][1]
        const data = items.filter(
          (item) => item.month === month && item.year === year
        )
        return { month, year, data }
      })
    : []

  useEffect(() => {
    fetch(require('./data.csv'))
      .then((response) => response.text())
      .then((data) => {
        const d = csvParse(data).map((item) => ({
          month: Number(item['mois']),
          year: Number(item['année']),
          text: item.texte,
          category: item['catégorie'],
          articles: item.articles,
        }))

        setItems(d)
        // console.log(d)
      })
  }, [])

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    setPct(
      (e.currentTarget.scrollLeft /
        (-contentRef?.offsetWidth! + window.innerWidth)) *
        -100
    )

    if (showTip && pct > 2) {
      setShowTip(false)
    }
  }

  const progressEvents = useRef([25, 50, 75, 100])

  progressEvents.current.forEach((progress) => {
    if (pct >= progress) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'scroll',
        event_label: `timeline-${progress}%`,
      })
      // console.log('sent', progress)
      progressEvents.current.shift()
    }
  })

  return (
    <Container>
      <ScrollContainer onScroll={handleScroll}>
        <Content
          ref={setContentRef}
          {...bind()}
          style={{ x }}
          $grabbing={grabbing}
        >
          {months
            .sort((a, b) => {
              if (a.year !== b.year) {
                return a.year - b.year
              } else {
                return a.month - b.month
              }
            })
            .map((month, index) => (
              <TimelineElement
                key={`timeline-element-${index}`}
                date={[month.year, month.month]}
                data={month.data}
              />
            ))}
          <GhostElement />
        </Content>
      </ScrollContainer>
      {transition(
        (style, item) =>
          item && (
            <Tip style={style}>
              <MdSwipeLeft />
            </Tip>
          )
      )}
      <ProgressBar pct={pct} />
    </Container>
  )
}
const Container = styled.div`
  position: relative;
  width: 100%;

  user-select: none;
  background-color: ${({ theme }) => theme.backgroundColor};
  color: ${({ theme }) => theme.fontColor};
  // -webkit-font-smoothing: antialiased;

  &:after {
    content: '';
    z-index: 1;
    display: block;
    width: 20vw;
    height: 100%;
    background: linear-gradient(
      to left,
      ${({ theme }) => theme.backgroundColor} 0%,
      rgba(0, 0, 0, 0) 100%
    );
    position: absolute;
    top: 0;
    right: 0;
    pointer-events: none;
  }
`

const ScrollContainer = styled.div`
  overflow: hidden;
  height: 60vh;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    overflow: hidden;
    overflow-x: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media ${device.tablet}, @media ${device.laptop} {
    overflow: hidden;
  }
`

const GhostElement = styled.div`
  width: 20vw;
`

const Content = styled(animated.div)<{ $grabbing: boolean }>`
  display: flex;
  height: 100%;
  cursor: ${({ $grabbing }) => ($grabbing ? 'grabbing' : 'grab')};
  width: max-content;
  touch-action: pan-y;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    touch-action: auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    touch-action: pan-y;
  }
`

const Tip = styled(animated.div)`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  z-index: 2;
  font-size: 4rem;
  background-color: ${({ theme }) => theme.invertedBackgroundColor};
  color: ${({ theme }) => theme.invertedFontColor};
  width: 6rem;
  height: 6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default ExampleComponent
